import React, { useEffect, useMemo } from 'react';
import { Button, Typography, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CheckCircleFilled } from '@ant-design/icons';
import { IconMessageCircle } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { getMyOrdersApi, getMyOrderDetailsApi } from "../../redux-toolkit/slices/orderCreamSlice";
import { formatDateMonthDayYear } from "../../helpers/date";
import { ReactComponent as AssessmentC } from '../../graphics/svgs/assessment-c.svg';
import MiniBodyViewer from "../MiniBodyViewer";

const StageOne = () => {
  // Render active order details
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orders, singleOrder } = useSelector((state: RootState) => state.orderCream);

  const latestOrder = useMemo(() => {
    if (Array.isArray(orders) && orders.length > 0) {
      return [...orders].sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        const validDateA = isNaN(dateA) ? 0 : dateA;
        const validDateB = isNaN(dateB) ? 0 : dateB;
        return validDateB - validDateA;
      })[0];
    }
    return null;
  }, [orders]);

  useEffect(() => {
    dispatch(getMyOrdersApi());
  }, [dispatch]);

  useEffect(() => {
    if (latestOrder) {
      const orderId = latestOrder._id || latestOrder.id;
      if (orderId) {
        dispatch(getMyOrderDetailsApi(`orderId=${orderId}`));
      }
    }
  }, [dispatch, latestOrder]);

  const checklistItems = [
    "Checking medical history",
    "Allergies and sensitivities review",
    "Contraindications and medication interaction check",
    "Identifying optimal pain relief formula",
    "Custom formula prescription issued"
  ];

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section  Start */}
      <div className="mb-6 relative max-w-[800px] mx-auto w-full">
        <div className="flex justify-between items-start">
          <h2 className="text-2xl md:text-3xl font-bold mb-1 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)', fontWeight: 'var(--font-h2-fontWeight)' }}>Hi {latestOrder?.shippingDetails?.firstName}! Track your order status here.
          </h2>
          <Button 
            size="large"
            className="rounded-lg font-semibold shadow-none flex items-center gap-2 hover:!bg-[var(--primary-500)] hover:!text-white hover:!border-[var(--primary-500)] transition-colors duration-300"
            icon={<IconMessageCircle size={20} />}
            onClick={() => navigate('/chat')}
            style={{ 
              borderColor: 'var(--primary-500)',
              color: 'var(--primary-500)',
              backgroundColor: 'transparent',
              fontFamily: 'var(--font-btn-fontFamily)',
              height: '44px',
              padding: '0 24px',
              fontSize: '14px'
            }}
          >
            Message care team
          </Button>
        </div>
        <div className="flex flex-col gap-0">
                  <div className="mb-4">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <Typography 
                            className="h6 text-theme-text-secondary" 
                            style={{ 
                              border: '1px solid var(--neutral-600)',
                              padding: '6px 16px',
                              borderRadius: '9999px',
                              lineHeight: '1',
                              display: 'inline-block',
                              textAlign: 'left'
                            }}
                          >
                            Rx # {latestOrder?.processingDetails?.orderNumber} ordered on {latestOrder?.createdAt ? formatDateMonthDayYear(latestOrder.createdAt) : "December 12, 2025"}
                          </Typography>
                        </div>
                      </div>
        </div>
      </div>
      {/* Header Section  End */}
      
      {/* Stage information content start*/}
      <div 
        className="mx-auto rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 w-full overflow-hidden relative max-w-[1200px]" 
        style={{ backgroundColor: "var(--neutral-100)" }}
      >
        <div className="flex flex-row items-start gap-6 mb-8">
          <div 
            className="flex-shrink-0 overflow-hidden border-4 border-white shadow-md"
            style={{ width: '96px', height: '96px', borderRadius: '50%' }}
          >
            <AssessmentC style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="flex flex-col justify-center pt-2">
          <h2 className="text-2xl md:text-3xl font-bold mb-1 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)', fontWeight: 'var(--font-h2-fontWeight)' }}>Show this container if stage NEW : Assessment is under review.
          </h2>
            <p className="text-lg text-[var(--theme-text-secondary)] leading-relaxed mb-4 max-w-3xl" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
             Dr. Shareef is currently reviewing your answers to determine if you're clinically a good fit for a personalized pain relief cream to help better manage your current pain level of {latestOrder?.painProfile?.painRating}.
            </p>
          </div>
        </div>

        <Row gutter={[48, 24]} className="mt-8">
          {/* Checklist */}
          <Col xs={24} md={12}>
            <div className="flex flex-col gap-0">
                          <p className="text-base text-[var(--theme-text-secondary)] font-medium" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
              This clinical review is a standard safety protocol for all prescription requests.
            </p>
              {checklistItems.map((item, index) => (
                <div key={index} className="mb-4">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <Typography 
                      className="h6 text-theme-text-secondary" 
                      style={{ 
                        border: '1px solid var(--neutral-600)', 
                        padding: '6px 16px', 
                        borderRadius: '9999px', 
                        lineHeight: '1', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        textAlign: 'left' 
                      }} 
                    >
                      <CheckCircleFilled style={{ color: 'var(--primary-500)', fontSize: '18px' }} />
                      {item}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </Col>
          
          {/* Pain Story & Location Visualization */}
          {singleOrder && (
            <Col xs={24} md={12}>
              <div className="header-content mb-6">
                <h4 className="h4 mb-2" style={{ textAlign: 'left' }}>Pain Story & Location</h4>
              </div>
              <div className="flex flex-col gap-3 w-full">
                <Row gutter={[24, 24]} align="top">
                  {/* Left Column: MiniBodyViewer */}
                  <Col xs={24} md={10} lg={8} className="flex justify-center">
                    <MiniBodyViewer isBodyClickEnable={false} painLocations={singleOrder?.painProfile?.painLocations || []} />
                  </Col>

                  {/* Right Column: Pain Story Content */}
                  <Col xs={24} md={14} lg={16}>
                    <div className="space-y-4">
                      {/* Pain Rating Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painRating === 0 ? (
                            <>Current pain level: 0/10 - No pain</>
                          ) : singleOrder.painProfile.painRating >= 1 && singleOrder.painProfile.painRating <= 3 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Mild pain</>
                          ) : singleOrder.painProfile.painRating >= 4 && singleOrder.painProfile.painRating <= 6 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Moderate pain</>
                          ) : singleOrder.painProfile.painRating >= 7 && singleOrder.painProfile.painRating <= 8 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Severe pain</>
                          ) : singleOrder.painProfile.painRating >= 9 && singleOrder.painProfile.painRating <= 10 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Extreme pain</>
                          ) : (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Duration Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDuration === "Acute - less than 3 months" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 3-6 months" ? (
                            <>Pain started: 3-6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 6+ months" ? (
                            <>Pain started: Over 6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 1+ years" ? (
                            <>Pain started: More than 1 year ago</>
                          ) : singleOrder.painProfile.painDuration === "acute" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : (
                            <>Pain started: More than 3 months ago</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Descriptions Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDescriptions.length === 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions[0]} sensation</>
                          ) : singleOrder.painProfile.painDescriptions.length > 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions.slice(0, -1).join(", ")}, and {singleOrder.painProfile.painDescriptions[singleOrder.painProfile.painDescriptions.length - 1]} sensations</>
                          ) : (
                            <>How it feels: No specific descriptions provided</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Location Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painLocations.length === 1 ? (
                            <>Pain location: {singleOrder.painProfile.painLocations[0]}</>
                          ) : singleOrder.painProfile.painLocations.length > 1 ? (
                            <>Pain locations: {singleOrder.painProfile.painLocations.join(", ")}</>
                          ) : (
                            <>Pain location: No specific locations provided</>
                          )}
                        </Typography>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
        </Row>

        {/* What's Next Section */}
        <div className="mt-16 mb-10">
          <h4 className="text-xl font-bold mb-3 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)' }}>What's next?</h4>
          <p className="text-lg text-[var(--theme-text-secondary)] leading-relaxed max-w-4xl" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
          Within 24 hours your assessment will be reviewed and responded to.
          <br />
          After your prescription is issued, you will see information about the ingredients and how it targets your pain. During this time your cream will be compounded and shipped.
          </p>
        </div>

        {/* Placeholder for Full Assessment */}
        <div className="mt-8 pt-6 border-t border-[var(--neutral-200)] text-left">
            <span className="uppercase text-sm font-bold tracking-widest text-[var(--theme-text-secondary)] opacity-60" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
                View Full Assessment
            </span>
        </div>

      </div>
      {/* Stage information content end*/}

    </div>
  );
};

export default StageOne;
